/**
 * The "starting soon" dialog.
 *
 * One at a time, soonest first: two meetings at once is rare, and stacking
 * dialogs asks you to make two decisions before you can see the page at all.
 * Deal with the nearer one and the next appears.
 *
 * The chime plays when a reminder first appears, not on every re-render — the
 * countdown re-renders this component every fifteen seconds, and a chime on
 * each would be intolerable.
 */

import { useEffect, useRef } from 'react'

import type { CalendarEvent } from '@/api/types'
import { SNOOZE_CHOICES, reminderKey, secondsUntil } from '@/hooks/useEventReminders'
import { playChime } from '@/lib/chime'
import { readableTextOn } from '@/lib/color'
import { parseTimestamp } from '@/lib/format'

function countdown(seconds: number): string {
  if (seconds <= 0) return 'starting now'
  const minutes = Math.floor(seconds / 60)
  if (minutes === 0) return `in ${Math.max(1, Math.round(seconds))} seconds`
  return `in ${minutes} minute${minutes === 1 ? '' : 's'}`
}

function label(leadSeconds: number): string {
  return leadSeconds === 0 ? 'At start time' : `${leadSeconds / 60} min before`
}

export function EventReminder({
  event,
  now,
  colour,
  feedName,
  onDismiss,
  onSnooze,
}: {
  event: CalendarEvent
  now: number
  colour: string
  feedName: string | undefined
  onDismiss: () => void
  onSnooze: (leadSeconds: number) => void
}) {
  const key = reminderKey(event)
  const chimedFor = useRef<string | null>(null)
  const dismissRef = useRef<HTMLButtonElement>(null)

  // Keyed on the event, not on mount: when one reminder is dealt with and the
  // next takes its place, this component stays mounted and the new one still
  // deserves its own chime.
  useEffect(() => {
    if (chimedFor.current === key) return
    chimedFor.current = key
    playChime()
    dismissRef.current?.focus()
  }, [key])

  // Escape dismisses. Deliberately the same as the button rather than a
  // silent close: a reminder that vanishes without recording a decision would
  // reappear on the next tick, which reads as broken.
  useEffect(() => {
    function onKey(keyEvent: KeyboardEvent) {
      if (keyEvent.key === 'Escape') {
        keyEvent.preventDefault()
        onDismiss()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onDismiss])

  const lead = secondsUntil(event, now)
  const start = parseTimestamp(event.starts_at).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })

  // Only offer to be reminded again at a point that has not already passed.
  const choices = SNOOZE_CHOICES.filter((choice) => choice < lead - 5)

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      // No backdrop-to-close: closing without a decision would re-raise the
      // dialog seconds later.
      role="presentation"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="reminder-title"
        aria-describedby="reminder-when"
        className="card w-full max-w-sm overflow-hidden shadow-xl"
      >
        <div
          className="px-4 py-2 text-xs font-semibold uppercase tracking-wide"
          style={{ backgroundColor: colour, color: readableTextOn(colour) }}
        >
          {feedName ?? 'Calendar'}
        </div>

        <div className="p-4">
          <p id="reminder-when" className="text-sm font-semibold text-navy">
            {countdown(lead)}
          </p>
          <h2 id="reminder-title" className="mt-0.5 text-lg font-bold">
            {event.summary || 'Untitled'}
          </h2>
          <p className="mt-0.5 text-sm text-ink-2">{start}</p>
          {event.location && (
            <p className="mt-1 break-words text-sm text-ink-3">{event.location}</p>
          )}

          {choices.length > 0 && (
            <>
              <p className="mt-4 text-xs font-semibold text-ink-2">Remind me again</p>
              <div className="mt-1 flex flex-wrap gap-2">
                {choices.map((choice) => (
                  <button
                    key={choice}
                    type="button"
                    className="btn btn-neutral btn-sm"
                    onClick={() => onSnooze(choice)}
                  >
                    {label(choice)}
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="mt-4 flex justify-end">
            <button
              ref={dismissRef}
              type="button"
              className="btn btn-primary btn-sm"
              onClick={onDismiss}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
